import React, { FC } from "react";
import styled from "styled-components";
import { graphql } from "gatsby";
import Img from "gatsby-image";
import { MDXRenderer } from "gatsby-plugin-mdx";

import Header from "src/components/Header";
import Heading, { HeadingScope, HeadingVariant } from "src/components/Heading";
import Button, { ButtonAppearance } from "src/components/Button";
import Layout from "src/components/Layout";
import Footer from "src/components/Footer";
import { colors } from "src/themes/colors";
import { mediaQuery } from "src/themes/breakpoints";

const ImgWrapper = styled.div`
  ${mediaQuery("lg")} {
    height: 500px;
  }
`;

const StyledImg = styled(Img)`
  max-height: 100%;
`;

const Title = styled(Heading)`
  margin: 20px auto 0;
  max-width: 780px;
  text-align: center;

  ${mediaQuery("lg")} {
    margin: 80px auto 20px;
  }
`;

const Wrapper = styled.div`
  margin: 0 auto;
  max-width: 900px;
`;

const PostInfo = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  justify-content: space-between;
  margin-top: 10px;

  ${mediaQuery("lg")} {
    margin: 80px auto 20px;
  }
`;

const Tags = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const Tag = styled.span`
  color: ${colors.orange500};
  font-weight: 500;
  margin-left: 10px;
`;

const ButtonWrapper = styled.div`
  margin: 40px 0 0;
  max-width: 200px;
`;

interface BlogPostProps {
  data: any;
}

const BlogPost: FC<BlogPostProps> = ({ data }) => {
  const {
    body,
    frontmatter: { title, featuredImage, date, tags },
  } = data.content;

  return (
    <HeadingScope>
      <Header isWhite />
      <Layout layout="Post">
        <ImgWrapper>
          <StyledImg fluid={featuredImage.childImageSharp.fluid} alt={title} />
        </ImgWrapper>
        <Title variant={HeadingVariant.ALFA}>{title}</Title>
        <PostInfo>
          <time dateTime={date}>Published on: {date}</time>
          {tags && (
            <Tags>
              Tags:{" "}
              {tags.map((tag, idx) => (
                <Tag key={idx}>{tag}</Tag>
              ))}
            </Tags>
          )}
        </PostInfo>
        <Wrapper>
          <MDXRenderer>{body}</MDXRenderer>
        </Wrapper>
        <ButtonWrapper>
          <Button appearance={ButtonAppearance.ALFA} url="/news">
            Zurück
          </Button>
        </ButtonWrapper>
      </Layout>
      <Footer />
    </HeadingScope>
  );
};

export default BlogPost;

export const pageQuery = graphql`
  query BlogPostByPath($path: String!) {
    content: mdx(frontmatter: { slug: { eq: $path } }) {
      body
      frontmatter {
        date(formatString: "YYYY-MM-DD")
        slug
        title
        tags
        featuredImage {
          childImageSharp {
            fluid(maxWidth: 1600) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    }
  }
`;
